import type { ICalculatedMetric, IReport, IReportColumnDef } from '../report.interface';
import type { IReportingMetadata } from '../hooks/useReportingMetadata';
import { getNumericSortCompare } from './gridQuery.adapters';
import {
    DEFAULT_TEXT_NUMERIC_DIMENSION_FORMAT,
    eventColumnKeyFromEventName,
    predictedColumnKeyFromEventName,
    predictedErrorColumnKeyFromEventName,
    predictedRemainingColumnKeyFromEventName,
    eventNameFromMetricKey,
    eventNameFromPredictedMetricKey,
    isEventMetricKey,
    isPredictedMetricKey,
    FREQUENCY_FORMAT,
    IMPRESSION_FREQUENCY_METRIC,
    REACH_FORMAT,
    REACH_METRIC,
} from '../reports';

/**
 * Metric-picker key -> projected column key in the response row.
 *
 * Constraints must be expressed in these column keys, so they resolve through here too:
 * a key derived any other way can name a column the backend does not project, which is
 * a 400 rather than a filtered report.
 */
export const metricColumnName = (eventType: string): string =>
    isEventMetricKey(eventType)
        ? eventColumnKeyFromEventName(eventNameFromMetricKey(eventType))
        : eventType;

export interface IReportColumnDeps {
    report: IReport;
    metadata: IReportingMetadata;
    campaignCalculatedMetrics: ICalculatedMetric[];
}

export const buildReportColumns = (
    data: any,
    deps: IReportColumnDeps,
): IReportColumnDef[] => {
    const { report, metadata, campaignCalculatedMetrics } = deps;
    if (!data) return [];

    const cols: IReportColumnDef[] = [];

    const customLabelMap = new Map((report.customLabels || []).map(item => [item.key, item.value]));

    const numericKeys = new Set<string>([
        ...metadata.metrics.map((m) => m.key),
        ...metadata.dimensions.filter((d) => d.type === 'integer').map((d) => d.key),
    ]);

    const dateKeys = new Set(
        metadata.dimensions.filter((d) => d.type === 'date').map((d) => d.key),
    );
    const dimensionKeys = new Set(metadata.dimensions.map((d) => d.key));

    for (const column of Object.keys(data)) {
        const options: IReportColumnDef['options'] = { filter: true, sort: true };
        const columnDefinition: IReportColumnDef = {
            name: column,
            label: customLabelMap.get(column) || metadata.labels.get(column) || column,
            options,
        };

        // Longest prefix first: 'predicted_' would otherwise swallow a predicted_remaining_ column.
        if (!dimensionKeys.has(column) && column.startsWith('predicted_')) {
            const isRemaining = column.startsWith('predicted_remaining_');
            const isError = column.startsWith('predicted_error_pct_');
            const selectedPredictedMetric = report.metrics.find((m) => {
                if (!isPredictedMetricKey(m.eventType)) return false;
                const eventName = eventNameFromPredictedMetricKey(m.eventType);
                if (isRemaining) return predictedRemainingColumnKeyFromEventName(eventName) === column;
                if (isError) return predictedErrorColumnKeyFromEventName(eventName) === column;
                return predictedColumnKeyFromEventName(eventName) === column;
            });
            if (selectedPredictedMetric) {
                // The metric's own label already reads "Predicted <event>".
                columnDefinition.label = isRemaining
                    ? `Remaining ${selectedPredictedMetric.label}`
                    : isError
                        ? `${selectedPredictedMetric.label} ± %`
                        : selectedPredictedMetric.label;
                // A filter names a dimension, which no prediction is, and the backend rejects it.
                // Sorting is left on: the estimate is materialised and projected under its own alias,
                // so ORDER BY names it and the ordering is the report's rather than the page's.
                options.sortCompare = getNumericSortCompare();
                options.filter = false;
                // Marked rather than sniffed by name: the grid colours it by band, and matching on a
                // prefix there would catch any future column that happens to end in a percentage.
                columnDefinition.isErrorPct = isError;
                // Not sortable, unlike the total beside it. The total is materialised into
                // conversion_prediction and projected under its own alias, so ORDER BY can name it;
                // the margin of error is derived during the merge and does not exist in SQL.
                if (isError) options.sort = false;
                cols.push(columnDefinition);
                continue;
            }
        }

        // event_date shares the event-metric prefix; unguarded it labels itself 'date'.
        if (!dimensionKeys.has(column) && column.startsWith('event_')) {
            const selectedEventMetric = report.metrics.find(
                (m) => metricColumnName(m.eventType) === column,
            );
            columnDefinition.label = selectedEventMetric?.label
                || customLabelMap.get(column)
                || column.slice('event_'.length);
            options.sortCompare = getNumericSortCompare();
            cols.push(columnDefinition);
            continue;
        }

        const sampleValue = data[column];
        const sniffedNumeric =
            typeof sampleValue === 'number'
            || (typeof sampleValue === 'string'
                && sampleValue.trim() !== ''
                && !Number.isNaN(Number.parseFloat(sampleValue))
                && !/[%:/-]/.test(sampleValue));
        if (numericKeys.has(column) || sniffedNumeric) {
            options.sortCompare = getNumericSortCompare();
        }

        if (dateKeys.has(column)) {
            cols.unshift(columnDefinition);
        } else {
            cols.push(columnDefinition);
        }
    }

    const newCalcByKey = new Map(
        campaignCalculatedMetrics
            .filter((c) => c.target_mode === 'new')
            .map((c) => [c.key, c]),
    );
    const overrideByTarget = new Map(
        campaignCalculatedMetrics
            .filter((c) => c.target_mode === 'override' && c.target_column)
            .map((c) => [c.target_column!, c]),
    );

    for (const col of cols) {
        const override = overrideByTarget.get(col.name);
        if (override) {
            col.label = override.display_label;
            col.format = override.format;
        }
        const newCalc = newCalcByKey.get(col.name);
        if (newCalc) {
            col.label = newCalc.display_label;
            col.options.sortCompare = getNumericSortCompare();
            col.format = newCalc.format;
        }
        if (!override && (col.name === 'c4' || col.name === 'c5')) {
            col.format = DEFAULT_TEXT_NUMERIC_DIMENSION_FORMAT;
        }
        if (!override && !newCalc && col.name === REACH_METRIC) {
            col.format = REACH_FORMAT;
            col.options.sortCompare = getNumericSortCompare();
        }
        if (!override && !newCalc && col.name === IMPRESSION_FREQUENCY_METRIC) {
            col.format = FREQUENCY_FORMAT;
            col.options.sortCompare = getNumericSortCompare();
        }
    }

    const colByName = new Map<string, IReportColumnDef>(cols.map((col) => [col.name, col]));
    const orderedNames: string[] = [];
    const pushName = (name: string) => {
        if (colByName.has(name) && !orderedNames.includes(name)) orderedNames.push(name);
    };

    const metricColumnNames = new Set<string>(
        report.metrics.map((m) => metricColumnName(m.eventType)),
    );
    ['served_impression_count', 'matched_impression_count', 'advertiser_event_count', 'revenue', 'cost', 'cpm', 'rpm', REACH_METRIC, IMPRESSION_FREQUENCY_METRIC].forEach(k => metricColumnNames.add(k));
    for (const col of cols) {
        if (metricColumnNames.has(col.name)) col.options.filter = false;
    }

    // Backend grouping order: any auto-added date leads, then the user's picks.
    for (const name of Object.keys(data)) {
        if (dimensionKeys.has(name)) pushName(name);
    }
    for (const dimension of report.dimensions) pushName(dimension.key);
    for (const metric of report.metrics) {
        pushName(metricColumnName(metric.eventType));
    }
    for (const col of cols) pushName(col.name);

    return orderedNames.map((name) => colByName.get(name)!);
};
